// Centralized runtime for the GeoJSON overlay layers that MapView
// pulls from edge proxies (fires / TFR / SUA / VIIRS / AIRMET / SIGMET
// / G-AIRMET / CWA / wxstations / PIREP).
//
// Why this module exists: every one of those layers used to ship a
// near-identical ~14-line refreshXxx() function that did:
//   1. Read the current map instance (defaulting to module-global).
//   2. setLoading(statusKey, true) on the layerStatusStore.
//   3. Build a URL (with or without a bbox), fetch, throw on !ok.
//   4. Parse JSON, stash in a module-level cache for setStyle restore.
//   5. setData() on the matching GeoJSONSource.
//   6. setUpdated/setError on the status store, then setLoading false.
//
// Plus each had its own boolean `xxxLoaded` flag for lazy-load gating.
// 10 of those across MapView.tsx is 250 lines of essentially-the-same
// code. This module collapses everything to a typed registry +
// `refreshOverlay()` + `markOverlayLoaded()` API.
//
// Subtleties preserved:
//   * Per-overlay URL builder closures (so AWC bbox-axis swap on
//     flightcat / pireps stays localized).
//   * Per-overlay fetch init (TFR uses `cache: 'no-store'`).
//   * Module-global last-fetched cache (pushCachedOverlays() restores
//     into freshly-created sources after a basemap setStyle()).
//   * statusKey distinct from sourceId (the LayerStatusStore uses
//     short keys like 'tfr' / 'gfa' while map sources use 'tfr' /
//     'gfa' but those happen to match \u2014 we still pass both
//     explicitly so a future divergence is easy).
//
// Heliports + wxcams are NOT here: they're structurally different
// (z>=10 gate + manual feature mapping), they live in MapView still.

import type { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import { useLayerStatusStore, type LayerKey } from '../stores/layerStatusStore';
import { safeGetSource } from './MapView';

/** Static descriptor for one GeoJSON overlay. */
export interface OverlaySpec {
  /** Identifier used in this module's caches + loaded-flag map. */
  id: OverlayId;
  /** GeoJSONSource id on the map. */
  sourceId: string;
  /** LayerStatusStore key (loading / updated / error). */
  statusKey: LayerKey;
  /** Build the fetch URL. Receives the map instance so bbox-aware
   *  overlays can inspect the current viewport. */
  buildUrl: (map: MlMap) => string;
  /** Optional fetch init (e.g. `cache: 'no-store'` for TFRs). */
  fetchInit?: RequestInit;
}

export type OverlayId =
  | 'fires'
  | 'tfr'
  | 'sua'
  | 'viirs'
  | 'airmet'
  | 'sigmet'
  | 'gfa'
  | 'cwa'
  | 'flightcat'      // wxstations source (METAR + TAF)
  | 'pireps'
  | 'dispatch'       // NIFC dispatch-zone polygons
  | 'obstacles'      // FAA DOF tall obstacles
  | 'repeaters'      // NIFC aviation-frequency repeater / receiver sites
  | 'classBCD'       // FAA Class B / C / D controlled airspace
  | 'airways'        // FAA ATS Route centerlines (Victor / Jet / RNAV)
  | 'airport-da';    // Density altitude halo per airport (METAR + ISA fallback)

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

/** Module-local cache of the last good FeatureCollection per overlay.
 *  Used by pushCachedOverlays() after setStyle() wipes sources.
 *  Also persisted to IDB so tab eviction / cold start can restore
 *  in-memory overlays without a network fetch (OXC3). */
const lastById = new Map<OverlayId, GeoJSON.FeatureCollection>();

// IDB persistence for overlay last-good cache.
import { kvGet, kvSet } from '../lib/idb';

const IDB_OVERLAY_KEY = 'fyrefly:overlays:lastById:v1';
// Bounded set of overlays worth persisting. Excludes large national
// datasets (fires/viirs) that are bbox-specific and not useful cold.
const PERSIST_OVERLAY_IDS = new Set<OverlayId>([
  'tfr', 'sua', 'airmet', 'sigmet', 'gfa', 'cwa', 'pireps',
  'classBCD', 'airways', 'dispatch', 'obstacles',
]);

/** Persist overlay cache entry to IDB. Non-fatal. */
function persistOverlay(id: OverlayId, gj: GeoJSON.FeatureCollection): void {
  if (!PERSIST_OVERLAY_IDS.has(id)) return;
  kvGet<Record<string, GeoJSON.FeatureCollection>>(IDB_OVERLAY_KEY)
    .then((stored) => {
      const map = stored ?? {};
      map[id] = gj;
      return kvSet(IDB_OVERLAY_KEY, map);
    })
    .catch(() => { /* non-fatal */ });
}

/** Rehydrate overlay cache from IDB at module load. */
kvGet<Record<string, GeoJSON.FeatureCollection>>(IDB_OVERLAY_KEY)
  .then((stored) => {
    if (!stored) return;
    for (const [id, gj] of Object.entries(stored)) {
      if (!lastById.has(id as OverlayId)) {
        lastById.set(id as OverlayId, gj);
      }
    }
  })
  .catch(() => { /* non-fatal */ });

/** Lazy-load flag per overlay. Heavy / opt-in overlays only fire upstream
 *  once the user explicitly toggles them on the first time. */
const loadedById = new Map<OverlayId, boolean>();

export function isOverlayLoaded(id: OverlayId): boolean {
  return loadedById.get(id) === true;
}
export function markOverlayLoaded(id: OverlayId, value = true): void {
  loadedById.set(id, value);
}

/** Push the last cached FeatureCollection back into a freshly-created
 *  source. Called from MapView's styledata handler after setStyle()
 *  resets the source data. */
export function pushCachedOverlays(map: MlMap, specs: ReadonlyArray<OverlaySpec>): void {
  for (const spec of specs) {
    const data = lastById.get(spec.id);
    if (!data) continue;
    const src = safeGetSource<GeoJSONSource>(map, spec.sourceId);
    src?.setData(data);
  }
}

/** Fetch + set + status-update for one overlay. Returns the parsed
 *  FeatureCollection on success (so callers like the LayerSheet refresh
 *  button can chain), or `null` on failure or when the map isn't ready. */
export async function refreshOverlay(
  map: MlMap | null | undefined,
  spec: OverlaySpec,
): Promise<GeoJSON.FeatureCollection | null> {
  if (!map) return null;
  const status = useLayerStatusStore.getState();
  status.setLoading(spec.statusKey, true);
  try {
    const url = spec.buildUrl(map);
    const r = await fetch(url, spec.fetchInit);
    if (!r.ok) throw new Error('HTTP ' + r.status);
    const gj = (await r.json()) as GeoJSON.FeatureCollection;
    lastById.set(spec.id, gj);
    persistOverlay(spec.id, gj);
    const src = safeGetSource<GeoJSONSource>(map, spec.sourceId);
    src?.setData(gj);
    status.setUpdated(spec.statusKey, Date.now(), gj.features?.length ?? 0);
    return gj;
  } catch (e: any) {
    status.setError(spec.statusKey, e?.message || 'fetch failed');
    return null;
  } finally {
    status.setLoading(spec.statusKey, false);
  }
}

/** Read-only access to the last-good payload for an overlay (for
 *  external callers like the SUA legend that want a count without
 *  triggering a fetch). */
export function getCachedOverlay(id: OverlayId): GeoJSON.FeatureCollection | null {
  return lastById.get(id) ?? null;
}

/** Manually stash a FeatureCollection \u2014 used by callers that fetch
 *  the data themselves (e.g. wxcams, heliports) but want to share the
 *  setStyle-restore plumbing. Pass null to clear. */
export function setCachedOverlay(
  id: OverlayId,
  data: GeoJSON.FeatureCollection | null,
): void {
  if (data) lastById.set(id, data);
  else lastById.delete(id);
}

/** Convenience exported for callers that need an empty FC to wire up
 *  a freshly-created source. */
export { EMPTY_FC };
